/**
 * Error helpers for the e-hentai / exhentai sources.
 *
 * Re-exports the typed taxonomy from `@/lib/errors` and adds a small
 * `detectErrorFromResponse` helper that consolidates the half-dozen ways
 * EhViewer's `EhEngine.kt` checks each response. The source modules call
 * this once per response before they try to parse.
 *
 * Notes on detection:
 *   - `cf-mitigated: challenge` and `__cf_chl_*` HTML are Cloudflare markers.
 *   - Empty body on `exhentai.org` is the canonical "Sad Panda" signal.
 *     e-hentai DOES sometimes return empty bodies on transient errors —
 *     we don't treat that as Sad Panda there.
 *   - 509 quota detection on image URLs uses `Url509Regex`, defined here
 *     so both `image-flow.ts` and parsers can share it.
 *   - A redirect chain ending at `bounce_login.php` is the e-hentai
 *     "insufficient GP" indicator (only used for "original image" links;
 *     we don't request originals, but the helper recognizes it anyway so
 *     the UI can surface it cleanly if it ever appears).
 */

import {
  AuthExpired,
  CloudflareChallenge,
  NetworkError,
  ParseError,
  QuotaExceeded,
  RateLimited,
  SadPanda,
  SourceError,
} from '@/lib/errors';

export {
  AuthExpired,
  CloudflareChallenge,
  NetworkError,
  ParseError,
  QuotaExceeded,
  RateLimited,
  SadPanda,
  SourceError,
};

/**
 * Insufficient GP for a download — distinct from the regular 4xx auth flow
 * because the user IS logged in, they just don't have enough GP. Modeled as
 * an `AuthExpired` subclass so the UI can keep the existing re-auth code
 * path while special-casing the message.
 */
export class InsufficientGp extends AuthExpired {}

/**
 * Matches the image URL the server returns when a user has hit their daily
 * image-bandwidth quota. EhViewer's exact pattern.
 *
 * @example
 *   Url509Regex.test('https://ehgt.org/g/509.gif')      // true
 *   Url509Regex.test('https://exhentai.org/img/509s.gif') // true
 *   Url509Regex.test('https://i.ehgt.org/x/y.jpg')      // false
 */
export const Url509Regex = /https:\/\/(?:ehgt\.org\/|exhentai\.org\/im)g\/509s?\.gif/;

const CloudflareChallengeBodyRegex = /__cf_chl_|\/cdn-cgi\/challenge-platform\//i;

export type EhHost = 'ehentai' | 'exhentai';

/**
 * Parse e-hentai's temporary-ban notice retry window — e.g.
 * "The ban expires in 13 hours and 44 minutes" — into a concrete retry hint.
 * Returns an empty object when no duration is present so callers can spread it
 * onto a {@link RateLimited} without introducing `undefined` keys.
 */
export function parseRetryWindow(text: string): { retryAt?: number; retryAfterText?: string } {
  const match = /expires in ([^.\n]+)/i.exec(text);
  if (!match || !match[1]) return {};
  const phrase = match[1].trim();
  let ms = 0;
  const hours = /(\d+)\s*hours?/i.exec(phrase);
  const minutes = /(\d+)\s*minutes?/i.exec(phrase);
  const seconds = /(\d+)\s*seconds?/i.exec(phrase);
  if (hours) ms += Number(hours[1]) * 3_600_000;
  if (minutes) ms += Number(minutes[1]) * 60_000;
  if (seconds) ms += Number(seconds[1]) * 1_000;
  if (ms <= 0) return { retryAfterText: phrase };
  return { retryAt: Date.now() + ms, retryAfterText: phrase };
}

/**
 * Inspect a {@link Response} (already-fetched, not yet read) and return a
 * matching typed error if anything is wrong. Returns `null` to mean "looks
 * good, proceed to parse".
 *
 * `bodyText` is optional — pass it when you've already read the body, so we
 * can do empty-body / known-marker detection without reading twice. If
 * omitted we only check headers + URL.
 */
export function detectErrorFromResponse(
  response: Response,
  host: EhHost,
  bodyText?: string,
): Error | null {
  const responseUrl = response.url || response.headers.get('x-upstream-url') || '';
  const location = response.headers.get('location') ?? '';

  if (response.headers.get('cf-mitigated') === 'challenge') {
    return new CloudflareChallenge(`Cloudflare challenge from ${host}`, { source: host });
  }
  if (bodyText !== undefined && CloudflareChallengeBodyRegex.test(bodyText)) {
    return new CloudflareChallenge(`Cloudflare challenge from ${host}`, { source: host });
  }

  if (Url509Regex.test(responseUrl)) {
    return new QuotaExceeded(`509 image quota exceeded (${responseUrl})`, { source: host });
  }

  if (
    host === 'exhentai' &&
    ((response.status >= 300 && response.status < 400) || response.status === 401) &&
    /forums\.e-hentai\.org\/remoteapi\.php/i.test(location)
  ) {
    return new SadPanda(
      'exhentai redirected through remoteapi — likely missing, stale, or incomplete ExHentai cookies.',
      { source: host },
    );
  }

  if (response.redirected) {
    const target = responseUrl;
    if (/bounce_login\.php/i.test(target)) {
      return new InsufficientGp(`bounce_login redirect from ${host}`, { source: host });
    }
    if (/Login|act=Login|sign_in|signin/i.test(target)) {
      return new AuthExpired(`Redirected to login from ${host}`, { source: host });
    }
  }

  if (response.status === 401 || response.status === 403) {
    return new AuthExpired(`HTTP ${response.status} from ${host}`, { source: host });
  }
  if (
    bodyText !== undefined &&
    /(?:Your|This) IP address has been temporarily banned/i.test(bodyText)
  ) {
    // The response identifies the request IP, not the user's account quota.
    // The transport layer adds whether that IP belongs to the proxy or client.
    return new RateLimited('Upstream temporarily rate-limited the request network path', {
      source: host,
      ...parseRetryWindow(bodyText),
    });
  }
  if (response.status === 429) {
    return new RateLimited(`HTTP 429 from ${host}`, { source: host });
  }

  if (bodyText !== undefined) {
    if (host === 'exhentai' && bodyText.trim() === '') {
      return new SadPanda(
        'exhentai returned an empty body — likely missing `igneous` cookie or forum-account access.',
        { source: host },
      );
    }
    if (
      /please log on/i.test(bodyText) ||
      /You need to log in/i.test(bodyText) ||
      (/Login Form/i.test(bodyText) && /act=Login/i.test(bodyText))
    ) {
      return new AuthExpired(`${host} response indicates user is logged out`, { source: host });
    }
  }

  return null;
}

/**
 * Re-throw helper that wraps a Response-shaped failure into the right typed
 * error. Use after fetch + body read when you want to fail fast on any
 * detection match. Returns the original body if no detection fires.
 */
export async function assertOk(response: Response, host: EhHost): Promise<string> {
  const text = await response.text();
  const err = detectErrorFromResponse(response, host, text);
  if (err) throw err;
  if (!response.ok) {
    throw new NetworkError(`HTTP ${response.status} from ${host}`, {
      source: host,
      status: response.status,
    });
  }
  return text;
}
